import { backendRequest } from './backendApi';

let accessCache = null;
let accessPromise = null;
const SELECTED_TENANT_STORAGE_KEY = 'qsafe:selectedTenantId';
const ACCESS_SNAPSHOT_STORAGE_KEY = 'qsafe:accessSnapshot';

function toPermissionSet(permissions = []) {
	const permissionList = permissions instanceof Set
		? Array.from(permissions)
		: Array.isArray(permissions)
			? permissions
			: [];

	return new Set(permissionList.map((permission) => String(permission).trim()).filter(Boolean));
}

function normalizeAccessSnapshot(payload = {}) {
	const userId = String(payload?.user_id || '').trim();
	const tenantId = String(payload?.tenant_id || '').trim();
	const roleId = String(payload?.role_id || '').trim();
	const role = String(payload?.role || '').trim();
	const permissions = toPermissionSet(payload?.permissions || []);

	if (!userId || !tenantId) {
		return null;
	}

	return {
		tenant_id: tenantId,
		user_id: userId,
		role_id: roleId || null,
		role: role || null,
		permissions,
	};
}

function readStorageJson(storageKey) {
	try {
		const raw = window.localStorage.getItem(storageKey);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function writeStorageJson(storageKey, payload) {
	try {
		if (payload == null) {
			window.localStorage.removeItem(storageKey);
			return;
		}

		window.localStorage.setItem(storageKey, JSON.stringify(payload));
	} catch {
		// Ignore storage failures.
	}
}

function readPreferredTenantId() {
	try {
		return window.localStorage.getItem(SELECTED_TENANT_STORAGE_KEY) || null;
	} catch {
		return null;
	}
}

function writePreferredTenantId(tenantId) {
	try {
		if (tenantId) {
			window.localStorage.setItem(SELECTED_TENANT_STORAGE_KEY, tenantId);
		} else {
			window.localStorage.removeItem(SELECTED_TENANT_STORAGE_KEY);
		}
	} catch {
		// Ignore storage failures.
	}
}

function persistAccessSnapshot(access) {
	const normalized = normalizeAccessSnapshot(access);
	if (!normalized) {
		writeStorageJson(ACCESS_SNAPSHOT_STORAGE_KEY, null);
		return;
	}

	writeStorageJson(ACCESS_SNAPSHOT_STORAGE_KEY, {
		tenant_id: normalized.tenant_id,
		user_id: normalized.user_id,
		role_id: normalized.role_id,
		role: normalized.role,
		permissions: Array.from(normalized.permissions),
	});
}

async function fetchMemberships() {
	const data = await backendRequest('/admin/rbac/me/memberships');
	return Array.isArray(data?.memberships) ? data.memberships : [];
}

function chooseTenantId(memberships = [], preferredTenantId = null) {
	if (!Array.isArray(memberships) || memberships.length === 0) {
		return null;
	}

	if (preferredTenantId && memberships.some((membership) => membership?.tenant_id === preferredTenantId)) {
		return preferredTenantId;
	}

	return memberships[0]?.tenant_id || null;
}

function shouldRetryWithMembershipTenant(error, preferredTenantId = null) {
	if (!preferredTenantId) {
		return false;
	}

	const status = Number(error?.status);
	const message = String(error?.message || error || '').toLowerCase();

	return (
		status === 400 ||
		status === 403 ||
		message.includes('multi-tenant') ||
		message.includes('requested tenant') ||
		message.includes('tenant switching')
	);
}

async function fetchAccessPayload(tenantId = null) {
	const suffix = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
	const data = await backendRequest(`/admin/rbac/me/access${suffix}`);
	if (data?.tenant_id) {
		writePreferredTenantId(data.tenant_id);
	}
	return data;
}

export async function getCurrentAccess({ forceRefresh = false } = {}) {
	if (!forceRefresh && accessCache) {
		return accessCache;
	}

	if (!forceRefresh && accessPromise) {
		return accessPromise;
	}

	accessPromise = (async () => {
		const preferredTenantId = readPreferredTenantId();
		let data;

		try {
			data = await fetchAccessPayload(preferredTenantId);
		} catch (error) {
			if (!shouldRetryWithMembershipTenant(error, preferredTenantId)) {
				throw error;
			}

			const memberships = await fetchMemberships();
			const resolvedTenantId = chooseTenantId(memberships, preferredTenantId);
			if (!resolvedTenantId) {
				throw error;
			}
			data = await fetchAccessPayload(resolvedTenantId);
		}

		const permissions = toPermissionSet(data?.permissions || []);
		const access = {
			tenant_id: data?.tenant_id || null,
			user_id: data?.user_id || null,
			role_id: data?.role_id || null,
			role: data?.role || null,
			permissions,
		};
		accessCache = access;
		persistAccessSnapshot(access);
		return access;
	})()
		.catch((error) => {
			accessCache = null;
			throw error;
		})
		.finally(() => {
			accessPromise = null;
		});

	return accessPromise;
}

export async function hasPermission(permissionCode, options = {}) {
	if (!permissionCode) {
		return false;
	}

	try {
		const access = await getCurrentAccess(options);
		return access.permissions.has(permissionCode);
	} catch {
		return false;
	}
}

export async function getCurrentTenantId(options = {}) {
	try {
		const access = await getCurrentAccess(options);
		return access.tenant_id || null;
	} catch {
		return null;
	}
}

export function getStoredAccessSnapshot(userId = null) {
	const snapshot = normalizeAccessSnapshot(readStorageJson(ACCESS_SNAPSHOT_STORAGE_KEY) || {});
	if (!snapshot) {
		return null;
	}

	if (userId && snapshot.user_id !== String(userId).trim()) {
		return null;
	}

	accessCache = snapshot;
	return snapshot;
}

export function clearAccessCache({ clearPersisted = false } = {}) {
	accessCache = null;
	accessPromise = null;

	if (clearPersisted) {
		writeStorageJson(ACCESS_SNAPSHOT_STORAGE_KEY, null);
	}
}
